import { FunctionComponent, ReactNode, useRef } from 'react'
import styled from '@emotion/styled'
import { DateTime } from 'luxon'
import { DayPicker, type Matcher } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { Grid, SvgIcon, Text, useClickOutsideEvent } from '@/tbui'
import 'react-day-picker/style.css'

export interface SingleDatePickerProps {
  date: Date | null
  focused: boolean
  id: string
  /** ISO date strings (YYYY-MM-DD) that may be selected; all other days are disabled. */
  availableDays: string[]
  customInput?: ReactNode
  disabled?: boolean
  onFocusChange: (arg: { focused: boolean | null }) => void
  onDateChange: (date: Date | null) => void
  onCancel: () => void
}

const DEFAULT_DATE_FORMAT = 'cccc, LLLL d, yyyy' // Luxon equivalent of 'dddd, MMMM D, YYYY'

const StyledWrapper = styled.div`
  position: relative;
`

const StyledDateInputWrapper = styled(Grid)`
  justify-content: space-between;
  cursor: pointer;
  padding: 0.5rem 21px;
`

const StyledDate = styled(Grid)`
  grid-template-areas: 'label icon' 'value icon';
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  > h6 {
    grid-area: label;
  }
  > p {
    grid-area: value;
  }
  > i {
    grid-area: icon;
  }
`

const StyledPopup = styled.div`
  position: absolute;
  /* Render above sibling form elements inside the modal. */
  z-index: 10;
  /* Match the prior react-dates positioning: float up-and-to-the-right of the
     input on desktop, snap toward the left edge on mobile. */
  bottom: 0px;
  left: 0px;
  background: ${(props) => props.theme.palette.WHITE};
  border-radius: ${(props) => props.theme.shape.RADIUS};
  box-shadow: ${(props) => props.theme.shape.SHADOW};
  padding: 12px;

  min-width: 300px;

  /* react-day-picker v9 class overrides — match the prior react-dates styling. */
  .rdp-root {
    --rdp-accent-color: ${(props) => props.theme.palette.PRIMARY};
    --rdp-day-height: 40px;
    --rdp-day-width: 40px;
    --rdp-day_button-height: 40px;
    --rdp-day_button-width: 40px;
    --rdp-day_button-border-radius: ${(props) => props.theme.shape.RADIUS};
    --rdp-day_button-border: none;
    --rdp-selected-border: none;
    --rdp-weekday-padding: 8px 0;
    --rdp-weekday-text-transform: uppercase;
  }

  .rdp-month_caption {
    font-family: ${(props) => props.theme.font.FAMILY_BOLD};
    font-size: 12.8px;
    justify-content: center;
  }

  .rdp-button_previous,
  .rdp-button_next {
    color: ${(props) => props.theme.palette.PRIMARY};
    cursor: pointer;
  }

  .rdp-button_previous:disabled,
  .rdp-button_next:disabled {
    cursor: revert;
    opacity: 0.4;
  }

  .rdp-weekday {
    color: ${(props) => props.theme.palette.TEXT};
    font-size: 11.2px;
    opacity: 1;
  }

  .rdp-day_button {
    color: ${(props) => props.theme.palette.TEXT};
    font-size: 11.2px;
  }

  .rdp-day_button:hover:not([disabled]) {
    color: ${(props) => props.theme.palette.TEXT_SECONDARY};
    background: ${(props) => props.theme.palette.SECONDARY};
  }

  /* Today: gray pill behind the date (only when not selected). */
  .rdp-today:not(.rdp-selected):not(.rdp-outside) .rdp-day_button {
    background: ${(props) => props.theme.palette.GRAY_1};
    color: ${(props) => props.theme.palette.TEXT};
  }

  /* Selected: solid primary fill, contrasting text. */
  .rdp-selected .rdp-day_button {
    background: ${(props) => props.theme.palette.PRIMARY};
    color: ${(props) => props.theme.palette.TEXT_PRIMARY};
    font-weight: bold;
  }

  .rdp-day_button[disabled] {
    color: ${(props) => props.theme.palette.GRAY_2};
    opacity: 1;
  }
`

const toIsoDate = (d: Date): string => DateTime.fromJSDate(d).toISODate() ?? ''

const SingleDatePicker: FunctionComponent<SingleDatePickerProps> = ({
  date,
  focused,
  id,
  availableDays,
  customInput,
  disabled,
  onDateChange,
  onFocusChange,
  onCancel,
}) => {
  const { t } = useTranslation()
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  useClickOutsideEvent(wrapperRef, onCancel)

  const handleInputClick = (): void => {
    if (disabled) {
      return
    }
    onFocusChange({ focused: !focused })
  }

  const handleSelect = (selected: Date | undefined): void => {
    if (selected != null) {
      onDateChange(selected)
    }
  }

  const disabledMatcher: Matcher = (day: Date) => !availableDays.includes(toIsoDate(day))

  const renderDefaultInput = (): ReactNode => (
    <StyledDateInputWrapper
      template="1fr auto"
      justify="center"
      align="center"
      data-test="date-picker-input"
      onClick={handleInputClick}
    >
      <StyledDate template="1fr auto">
        <Text type="H6" color="GRAY_2">
          {t('single_date_picker.label')}
        </Text>
        <Text fontSize="16px" data-test="formatted-date">
          {date != null ? DateTime.fromJSDate(date).toFormat(DEFAULT_DATE_FORMAT) : ''}
        </Text>
        <SvgIcon name="calendar" color="PRIMARY" fontSize="16px" />
      </StyledDate>
    </StyledDateInputWrapper>
  )

  return (
    <StyledWrapper ref={wrapperRef} id={id}>
      {customInput ?? renderDefaultInput()}
      {focused && (
        <StyledPopup data-test="date-picker-popup">
          <DayPicker
            mode="single"
            selected={date ?? undefined}
            onSelect={handleSelect}
            disabled={disabledMatcher}
            defaultMonth={date ?? undefined}
            showOutsideDays
            navLayout="around"
            labels={{
              labelNav: () => t('react_dates_calendar_label'),
              labelPrevious: () => t('react_dates_jump_to_prev_month'),
              labelNext: () => t('react_dates_jump_to_next_month'),
              labelWeekday: (d) => DateTime.fromJSDate(d).toFormat('cccc'),
              labelGrid: (d) => DateTime.fromJSDate(d).toFormat('LLLL yyyy'),
            }}
            components={{
              Chevron: ({ orientation }) => {
                // Inline SVG (no pointer-events:none from any wrapper). The
                // containing rdp button still owns the onClick; we just supply
                // the visual.
                const isRight = orientation === 'right'
                return (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d={isRight ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'}
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )
              },
            }}
          />
        </StyledPopup>
      )}
    </StyledWrapper>
  )
}

export default SingleDatePicker
